import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { getMediaUrl, chatAPI } from '../services/api';
import { Input } from './ui/input';
import { toast } from 'sonner';

const StoryViewer = ({ userStory, onClose }) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const stories = userStory?.stories || [];
  const currentStory = stories[currentStoryIndex];

  useEffect(() => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        // Don't advance if paused
        if (isPaused) {
          return prev;
        }
        
        if (prev >= 100) {
          clearInterval(interval);
          // Move to next story
          if (currentStoryIndex < stories.length - 1) {
            setCurrentStoryIndex(currentStoryIndex + 1);
          } else {
            onClose();
          }
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentStoryIndex, stories.length, onClose, isPaused]);

  const handlePrevious = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      onClose();
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);
    try {
      // Send reply as a direct message
      const messageContent = `📖 Replied to story: "${replyText}"`;
      await chatAPI.sendMessage(userStory.user._id || userStory.user.id, messageContent);
      
      toast.success(`Reply sent to ${userStory.user.name}`);
      setReplyText('');
      setIsPaused(false); // Resume story timer after sending
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
      setIsPaused(false); // Resume even on error
    } finally {
      setSending(false);
    }
  };

  const getTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const hours = Math.floor((new Date() - date) / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (!currentStory) {
    return null;
  }

  // Get media URL - stories have mediaUrls array
  const mediaUrl = currentStory.mediaUrls?.[0] || currentStory.images?.[0] || '';
  const isVideo = mediaUrl.includes('.mp4') || mediaUrl.includes('.mov') || 
                  mediaUrl.includes('.avi') || mediaUrl.includes('.webm');

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      <div className="relative w-full max-w-lg h-full md:h-[90vh] bg-black">
        {/* Progress Bars */}
        <div className="absolute top-0 left-0 right-0 z-10 p-2 flex gap-1">
          {stories.map((_, index) => (
            <div key={index} className="flex-1 h-1 bg-gray-600 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-100"
                style={{ 
                  width: index === currentStoryIndex ? `${progress}%` : index < currentStoryIndex ? '100%' : '0%',
                  backgroundColor: 'hsl(var(--primary))' 
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-4 left-0 right-0 z-10 flex items-center justify-between px-4 mt-4">
          <div className="flex items-center space-x-3">
            <Avatar className="w-10 h-10 border-2 border-white">
              <AvatarImage src={userStory.user.profileImage} alt={userStory.user.name} />
              <AvatarFallback>{userStory.user.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-semibold text-sm">{userStory.user.name}</p>
              <p className="text-white/80 text-xs">{getTimeAgo(currentStory.createdAt)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation Areas */}
        <div className="absolute inset-0 flex">
          <div 
            className="flex-1 cursor-pointer" 
            onClick={handlePrevious}
          />
          <div 
            className="flex-1 cursor-pointer" 
            onClick={handleNext}
          />
        </div>

        {/* Story Media */}
        <div className="w-full h-full flex items-center justify-center">
          {isVideo ? (
            <video
              src={getMediaUrl(mediaUrl)}
              className="max-w-full max-h-full object-contain"
              autoPlay
              playsInline
            />
          ) : (
            <img
              src={getMediaUrl(mediaUrl)}
              alt="Story"
              className="max-w-full max-h-full object-contain"
              loading="eager"
              decoding="async"
            />
          )}
        </div>

        {/* Navigation Arrows (Desktop) */}
        {currentStoryIndex > 0 && (
          <button
            onClick={handlePrevious}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors z-20"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        )}
        {currentStoryIndex < stories.length - 1 && (
          <button
            onClick={handleNext}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors z-20"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        )}

        {/* Reply Input - Instagram Style - Mobile Optimized with Bottom Nav Clearance */}
        <div className="absolute bottom-0 left-0 right-0 z-20 pb-20 md:pb-4">
          <div className="p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-12">
            <form onSubmit={handleReply} className="flex items-center gap-3">
              <Input
                type="text"
                placeholder={`Reply to ${userStory.user.name}...`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onFocus={() => setIsPaused(true)}
                onBlur={() => setIsPaused(false)}
                disabled={sending}
                className="flex-1 bg-white/20 border-white/40 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/60 rounded-full px-5 py-6 text-base"
              />
              <button
                type="submit"
                disabled={!replyText.trim() || sending}
                className="p-4 bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors flex-shrink-0"
              >
                {sending ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-6 h-6 text-white" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryViewer;
